import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Error as ErrorIcon,
  Memory as MemoryIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAgenticFlows, getFlowExecutions } from '../../api/agenticFlow.api';

function FlowSettingsTab({ flowSettings, setFlowSettings, versions = [], onRestoreVersion, currentFlowId }) {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [executions, setExecutions] = useState([]);
  useEffect(() => {
    getAgenticFlows({}).then(({ response }) => {
      const list = Array.isArray(response) ? response : (response?.flows ?? []);
      setFlows(list || []);
    });
  }, []);
  useEffect(() => {
    if (!currentFlowId) return;
    getFlowExecutions(currentFlowId).then(({ response }) => {
      const list = response?.executions ?? response?.data ?? [];
      setExecutions(Array.isArray(list) ? list : []);
    }).catch(() => setExecutions([]));
  }, [currentFlowId]);

  const handleHelpClick = () => {
    navigate('/agentic-workflow-help');
  };

  const handleSettingChange = (setting, value) => {
    setFlowSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  return (
    <Paper sx={{ p: 3, pb: 6 }}>
      {' '}
      {/* Add bottom padding for better scrolling */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6" gutterBottom>
          Flow Configuration
        </Typography>
        <Tooltip title="Agentic Workflow Help">
          <IconButton
            onClick={handleHelpClick}
            color="primary"
            size="small"
          >
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Configure execution settings, error handling, and performance options for your agentic workflow.
        </Typography>
      </Alert>

      <Stack spacing={3}>
        {/* Execution Settings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon color="primary" />
              <Typography>Execution Settings</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableParallelExecution}
                        onChange={(e) => handleSettingChange('enableParallelExecution', e.target.checked)}
                      />
                    )}
                    label="Enable parallel execution"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.autoSaveState}
                        onChange={(e) => handleSettingChange('autoSaveState', e.target.checked)}
                      />
                    )}
                    label="Auto-save state"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableExecutionLogging}
                        onChange={(e) => handleSettingChange('enableExecutionLogging', e.target.checked)}
                      />
                    )}
                    label="Enable execution logging"
                  />
                  <TextField
                    label="Max execution time (seconds)"
                    type="number"
                    value={flowSettings.maxExecutionTime}
                    onChange={(e) => handleSettingChange('maxExecutionTime', parseInt(e.target.value) || 300)}
                    size="small"
                    fullWidth
                    helperText="Maximum time allowed for flow execution"
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableVariableSubstitution}
                        onChange={(e) => handleSettingChange('enableVariableSubstitution', e.target.checked)}
                      />
                    )}
                    label="Enable variable substitution"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableDebugMode}
                        onChange={(e) => handleSettingChange('enableDebugMode', e.target.checked)}
                      />
                    )}
                    label="Enable debug mode"
                  />
                  <TextField
                    label="Execution timeout (ms)"
                    type="number"
                    value={flowSettings.executionTimeout}
                    onChange={(e) => handleSettingChange('executionTimeout', parseInt(e.target.value) || 30000)}
                    size="small"
                    fullWidth
                    helperText="Timeout for individual node execution"
                  />
                  <TextField
                    label="LangGraph continuation timeout (seconds)"
                    type="number"
                    value={flowSettings.langgraphContinuationTimeout}
                    onChange={(e) => handleSettingChange('langgraphContinuationTimeout', parseInt(e.target.value) || 20)}
                    size="small"
                    fullWidth
                    helperText="Timeout for LangGraph execution (parallel nodes, API calls)"
                  />
                  <TextField
                    label="Background task timeout (seconds)"
                    type="number"
                    value={flowSettings.backgroundTaskTimeout}
                    onChange={(e) => handleSettingChange('backgroundTaskTimeout', parseInt(e.target.value) || 30)}
                    size="small"
                    fullWidth
                    helperText="Timeout for background form processing tasks"
                  />
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Error Handling */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon color="error" />
              <Typography>Error Handling</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>On Error</InputLabel>
                    <Select
                      value={flowSettings.onError}
                      onChange={(e) => handleSettingChange('onError', e.target.value)}
                      label="On Error"
                    >
                      <MenuItem value="stop">Stop execution</MenuItem>
                      <MenuItem value="continue">Continue with next node</MenuItem>
                      <MenuItem value="retry">Retry current node</MenuItem>
                      <MenuItem value="skip">Skip to error handler</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Max retry attempts"
                    type="number"
                    value={flowSettings.maxRetryAttempts}
                    onChange={(e) => handleSettingChange('maxRetryAttempts', parseInt(e.target.value) || 3)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <TextField
                    label="Retry delay (seconds)"
                    type="number"
                    value={flowSettings.retryDelay}
                    onChange={(e) => handleSettingChange('retryDelay', parseInt(e.target.value) || 5)}
                    size="small"
                    fullWidth
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.logAllErrors}
                        onChange={(e) => handleSettingChange('logAllErrors', e.target.checked)}
                      />
                    )}
                    label="Log all errors"
                  />
                  <FormControl size="small" fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Error workflow (optional)</InputLabel>
                    <Select
                      value={flowSettings.errorWorkflowId || ''}
                      onChange={(e) => handleSettingChange('errorWorkflowId', e.target.value || null)}
                      label="Error workflow (optional)"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {flows.map((f) => (
                        <MenuItem key={f._id || f.id} value={f._id || f.id}>
                          {f.name || 'Untitled Flow'}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      When execution fails, this workflow runs with error context (execution_id, error, node_id).
                    </Typography>
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* State Management */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MemoryIcon color="primary" />
              <Typography>State Management</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.persistStateBetweenRuns}
                        onChange={(e) => handleSettingChange('persistStateBetweenRuns', e.target.checked)}
                      />
                    )}
                    label="Persist state between runs"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableStateValidation}
                        onChange={(e) => handleSettingChange('enableStateValidation', e.target.checked)}
                      />
                    )}
                    label="Enable state validation"
                  />
                  <TextField
                    label="State cleanup interval (hours)"
                    type="number"
                    value={flowSettings.stateCleanupInterval}
                    onChange={(e) => handleSettingChange('stateCleanupInterval', parseInt(e.target.value) || 24)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.autoSaveStateVariables}
                        onChange={(e) => handleSettingChange('autoSaveStateVariables', e.target.checked)}
                      />
                    )}
                    label="Auto-save state variables"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableStateEncryption}
                        onChange={(e) => handleSettingChange('enableStateEncryption', e.target.checked)}
                      />
                    )}
                    label="Enable state encryption"
                  />
                  <TextField
                    label="Max state size (MB)"
                    type="number"
                    value={flowSettings.maxStateSize}
                    onChange={(e) => handleSettingChange('maxStateSize', parseInt(e.target.value) || 10)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Security Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon color="warning" />
              <Typography>Security & Access</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableSandboxedExecution}
                        onChange={(e) => handleSettingChange('enableSandboxedExecution', e.target.checked)}
                      />
                    )}
                    label="Enable sandboxed execution"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.requireAuthentication}
                        onChange={(e) => handleSettingChange('requireAuthentication', e.target.checked)}
                      />
                    )}
                    label="Require authentication"
                  />
                  <TextField
                    label="API rate limit (requests/min)"
                    type="number"
                    value={flowSettings.apiRateLimit}
                    onChange={(e) => handleSettingChange('apiRateLimit', parseInt(e.target.value) || 60)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableAuditLogging}
                        onChange={(e) => handleSettingChange('enableAuditLogging', e.target.checked)}
                      />
                    )}
                    label="Enable audit logging"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.validateInputData}
                        onChange={(e) => handleSettingChange('validateInputData', e.target.checked)}
                      />
                    )}
                    label="Validate input data"
                  />
                  <TextField
                    label="Session timeout (minutes)"
                    type="number"
                    value={flowSettings.sessionTimeout}
                    onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value) || 30)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Performance Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon color="success" />
              <Typography>Performance & Optimization</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableCaching}
                        onChange={(e) => handleSettingChange('enableCaching', e.target.checked)}
                      />
                    )}
                    label="Enable caching"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableCompression}
                        onChange={(e) => handleSettingChange('enableCompression', e.target.checked)}
                      />
                    )}
                    label="Enable compression"
                  />
                  <TextField
                    label="Cache TTL (seconds)"
                    type="number"
                    value={flowSettings.cacheTTL}
                    onChange={(e) => handleSettingChange('cacheTTL', parseInt(e.target.value) || 3600)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enablePerformanceMonitoring}
                        onChange={(e) => handleSettingChange('enablePerformanceMonitoring', e.target.checked)}
                      />
                    )}
                    label="Enable performance monitoring"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.optimizeMemoryUsage}
                        onChange={(e) => handleSettingChange('optimizeMemoryUsage', e.target.checked)}
                      />
                    )}
                    label="Optimize memory usage"
                  />
                  <TextField
                    label="Max concurrent nodes"
                    type="number"
                    value={flowSettings.maxConcurrentNodes}
                    onChange={(e) => handleSettingChange('maxConcurrentNodes', parseInt(e.target.value) || 5)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Integration Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudIcon color="info" />
              <Typography>Integration & External Services</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableApiCalls}
                        onChange={(e) => handleSettingChange('enableApiCalls', e.target.checked)}
                      />
                    )}
                    label="Enable API calls"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableWebhookNotifications}
                        onChange={(e) => handleSettingChange('enableWebhookNotifications', e.target.checked)}
                      />
                    )}
                    label="Enable webhook notifications"
                  />
                  <TextField
                    label="Default API timeout (seconds)"
                    type="number"
                    value={flowSettings.defaultApiTimeout}
                    onChange={(e) => handleSettingChange('defaultApiTimeout', parseInt(e.target.value) || 30)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableExternalDatabaseConnections}
                        onChange={(e) => handleSettingChange('enableExternalDatabaseConnections', e.target.checked)}
                      />
                    )}
                    label="Enable external database connections"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={flowSettings.enableEmailNotifications}
                        onChange={(e) => handleSettingChange('enableEmailNotifications', e.target.checked)}
                      />
                    )}
                    label="Enable email notifications"
                  />
                  <TextField
                    label="Webhook retry attempts"
                    type="number"
                    value={flowSettings.webhookRetryAttempts}
                    onChange={(e) => handleSettingChange('webhookRetryAttempts', parseInt(e.target.value) || 3)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Execution History */}
        {currentFlowId && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Execution history</Typography>
                <Chip label={`${executions.length} runs`} size="small" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Recent runs for this flow. View details in Flow Execution Tracker.
              </Typography>
              {executions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No executions yet. Run the flow to see history.</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {executions.slice(0, 10).map((ex) => (
                    <Stack key={ex.execution_id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          {ex.execution_id?.slice(0, 12)}…
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {ex.status === 'success' ? '✓' : '✗'}
                          {' '}
                          {ex.execution_time ? `${(ex.execution_time / 1000).toFixed(1)}s` : ''}
                          {' '}
                          {ex.created_at ? new Date(ex.created_at).toLocaleString() : ''}
                        </Typography>
                      </Box>
                      <Button size="small" href={`/flow-execution-tracker?executionId=${ex.execution_id}`} target="_blank" rel="noopener">
                        View
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Version History */}
        {versions?.length > 0 && onRestoreVersion && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Version history</Typography>
                <Chip label={`${versions.length} saved`} size="small" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Restore a previous version. Versions are created on each save (last 20 kept).
              </Typography>
              <Stack spacing={0.5}>
                {versions.slice(0, 5).map((v) => (
                  <Stack key={v.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="caption">{new Date(v.timestamp).toLocaleString()}</Typography>
                    <Button size="small" onClick={() => onRestoreVersion(v)}>
                      Restore
                    </Button>
                  </Stack>
                ))}
                {versions.length > 5 && (
                  <Typography variant="caption" color="text.secondary">… and {versions.length - 5} more</Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </Stack>

      {/* Quick Help Section */}
      <Card sx={{ mt: 3, bgcolor: 'primary.50' }}>
        <CardContent>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
          }}
          >
            <InfoIcon color="primary" />
            <Typography variant="h6">Need Help?</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Learn more about agentic workflows, node types, edge configurations, and best practices.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="Node Types Guide"
              onClick={handleHelpClick}
              color="primary"
              variant="outlined"
              clickable
            />
            <Chip
              label="Edge Configuration"
              onClick={handleHelpClick}
              color="primary"
              variant="outlined"
              clickable
            />
            <Chip
              label="LangGraph Backend"
              onClick={handleHelpClick}
              color="primary"
              variant="outlined"
              clickable
            />
          </Box>
        </CardContent>
      </Card>
    </Paper>
  );
}

export default FlowSettingsTab;
